<template>
    <form>
        <b-form-group label-cols="3" label="Client:" label-for="client">
            <vet-select
                id="client"
                ref="client"
                v-model="state.selectedClient"
                :show-menu="true"
                name="client"
                module="clients-include-inactive"
                v-validate="'required'"
                placeholder="Client name (required)"
                :class="{ 'border-danger': errors.has('client') }"
                hide-backdrop
            />
        </b-form-group>
        <b-form-group label-cols="3" label="Guest:" label-for="guest">
            <vet-select
                id="guest"
                ref="guest"
                v-model="state.selectedPatient"
                :show-menu="true"
                name="guest"
                module="client-patient-all"
                :showSpecies="true"
                v-validate="'required'"
                placeholder="Guest name (required)"
                :class="{ 'border-danger': errors.has('guest') }"
                hide-backdrop
                :client-id="selectedClientId"
                label="name"
            />
        </b-form-group>
        <b-form-group label-cols="3" label="Check In:" label-for="dateCheckIn">
            <date-picker
                name="dateCheckIn"
                :config="datePickerOptions"
                v-model="state.dateCheckIn"
                placeholder="Check-in date"
                @dp-change="value => updateMainBooking(value, 'in')"
            />
        </b-form-group>
        <b-form-group
            label-cols="3"
            label="Check Out:"
            label-for="dateCheckOut"
        >
            <date-picker
                name="dateCheckOut"
                :config="datePickerOptionsCheckOut"
                v-model="state.dateCheckOut"
                placeholder="Check-out date"
                @dp-change="value => updateMainBooking(value, 'out')"
            />
        </b-form-group>
        <b-form-group label-cols="3" label="Room:" label-for="room">
            <v-select
                id="room"
                name="room"
                :options="roomList"
                v-model="state.selectedRoom"
                placeholder="Room (required)"
                item-text="label"
            />
        </b-form-group>
        <b-form-group label-cols="3" label="Provider:" label-for="provider">
            <input
                v-if="isLoadingProvider"
                class="form-control"
                disabled
                placeholder="Loading..."
            />
            <v-select
                v-else
                id="provider"
                name="provider"
                :options="state.providerList"
                v-model="state.selectedProvider"
                placeholder="Provider (required)"
                item-text="label"
                :class="{ 'border-danger': errors.has('provider') }"
                v-validate="'required'"
            />
        </b-form-group>
        <b-form-group label-cols="3" label="Notes:" label-for="notes">
            <textarea
                class="form-control"
                placeholder="Notes (optional)"
                v-model="state.notes"
            />
        </b-form-group>
    </form>
</template>

<script>
import vSelect from "vue-select";
import Staff from "@/services/staff";
import vetSelect from "@/module/commons/vet-select.vue";

export default {
    components: {
        vSelect,
        vetSelect
    },
    computed: {
        isLoadingFooterPreview() {
            return this.$store.state.modalReservation.isLoadingFooterPreview;
        },
        datePickerOptionsCheckOut() {
            if (this.state.dateCheckIn) {
                let mMinDate = moment(this.state.dateCheckIn);

                if (!mMinDate.isValid()) {
                    return;
                }

                let minDate = mMinDate.startOf("day").toDate();

                return {
                    ...this.datePickerOptions,
                    minDate
                };
            }

            return this.datePickerOptions;
        },
        selectedClientId() {
            return this.state.selectedClient
                ? this.state.selectedClient.id
                : null;
        },
        state: {
            get() {
                return this.$store.state.modalReservation.view.RESERVATION;
            },
            set(value) {
                this.$store.commit("modalReservation/setViewState", {
                    view: "RESERVATION",
                    value: value
                });
            }
        },
        roomList() {
            return this.$store.state.modalReservation.roomList || [];
        },
        bookings: {
            get() {
                return this.$store.state.modalReservation.bookings;
            },
            set(value = []) {
                this.$store.commit("modalReservation/setBookings", value);
            }
        },
        checkInTime() {
            return this.$store.state.modalReservation.checkInTime;
        },
        checkOutTime() {
            return this.$store.state.modalReservation.checkOutTime;
        },
        isEditMode() {
            return this.$store.state.modalReservation.isEditMode;
        }
    },
    watch: {
        "state.selectedClient"(value) {
            let client = this.properClientData(value);
            this.$store.dispatch("modalReservation/applyClientData", client);
            this.clearPatient();
        },
        "state.selectedRoom"(value) {
            this.changeMainBookingRoom(value);
        }
    },
    data() {
        return {
            datePickerOptions: {},
            isLoadingProvider: true
        };
    },
    async mounted() {
        await this._getSetting();

        if (this.isEditMode) {
            this.fetchProviders(this.state.selectedProvider.id);
        } else {
            this.fetchProviders(this.$root.currentActor.id);
        }

        this.datePickerOptions = {
            format: this.getDateFormat(),
            timeZone: this._timeZone
        };

        // register check-in/out dates, room into bookings ref
        if (!this.bookings[0]) {
            this.registerBooking();
        }
    },
    methods: {
        properClientData(client) {
            let result = client;

            // handle reminder options if client is created via "Add Client" button
            let reminders = [];
            if (!result.reminderOptions) {
                for (let r of result.reminder_options) {
                    let value = Number(r);
                    if (isNaN(value)) {
                        continue;
                    }
                    reminders.push(value);
                }
                result.reminderOptions = reminders;
            }

            // handle proper birthdate - fix issue when creating new client via Add Client
            let dateString = result.birthdate;
            let dob = new Date(dateString);
            if (!isNaN(dob) && dob.toISOString().split("T")[0] === dateString) {
                result.birthdate = moment(dateString).format(
                    this.getDateFormat()
                );
            }

            return result;
        },
        clearPatient() {
            this.$store.commit("modalReservation/setPatient", null);
        },
        changeMainBookingRoom(room) {
            if (!room || !this.bookings[0]) {
                return;
            }

            let bookings = [...this.bookings];
            let mainBooking = bookings[0];
            mainBooking.roomId = room.id;
            mainBooking.roomName = room.label;

            let uidContents = mainBooking.uniqueId.split("_");
            mainBooking.uniqueId = [
                room.id,
                uidContents[1],
                uidContents[2]
            ].join("_");

            this.bookings = bookings;
        },
        updateMainBooking(value, type) {
            if (!value.date) {
                return;
            }

            let time = "00:00";
            if (type === "in") {
                time = this.checkInTime;
            } else if (type === "out") {
                time = this.checkOutTime;
            }

            let dateStr = value.date.format("YYYY-MM-DD");
            dateStr += " " + time;

            let stringFormat = "YYYY-MM-DD HH:mm";
            let momentDate = moment.tz(dateStr, stringFormat, this._timeZone); // prettier-ignore
            let valueDate = momentDate.toDate();

            let bookings = [...this.bookings];
            let mainBooking = bookings[0];

            if (mainBooking) {
                if (type === "in") {
                    mainBooking.start = valueDate;
                    mainBooking.startUnix = momentDate.unix();
                    this.state.dateCheckIn = valueDate; // see note¹
                } else if (type === "out") {
                    mainBooking.end = valueDate;
                    mainBooking.endUnix = momentDate.unix();
                    this.state.dateCheckOut = valueDate; // see note¹
                }
            }

            this.bookings = bookings; // replace

            // note¹ (malay):
            /*
            ini ada masalah dengan datepicker component yang kita guna.
            pakej ni dah lama, dan dah tak dimaintain pun. ref: https://jonathanpeterson.com/posts/state-of-my-picker
            isu dia sekarang ni, bila user tukar tarikh, tarikh yang dipilih tu akan disimpan dalam bentuk string.
            kemudian tarikh string tersebut disimpan dalam vuex (dalam kes wizard ni lah)
            bila user go next, kemudian back dalam wizard ni,
            value tarikh string tu akan dipass semula ke komponen datepicker, dalam bentuk string.
            datepicker tersebut akan anggap tarikh string itu dalam local timezone (ikut komputer).
            kemudian baru dia convert ke timezone yang kita setkan (sila lihat mounted).
            maka datepicker akan convert tarikh string tadi (local) kepada tarikh ikut timezone yang kita tetapkan.
            dekat sinilah punca dia. kalau dari timezone KL ke Los Angeles, maka jadilah tarikh sehari ke belakang.

            solusi cadangan 1: guna option `parseInputDate` untuk convert date string ke date object.
            tapi tak boleh pakai sebab library ni memang ada error/bug. lebih kurang sama masalahnya di sini:
            https://github.com/Eonasdan/tempus-dominus/issues/1339#issuecomment-332511815

            solusi cadangan 2: convert value dalam dp-change, kemudian masukkan semula ke datepicker tu.
            so dia akan jadi macam ni, date string --> date object --> replace value date string tadi.
            yang ini solusi kita pakai untuk kekalkan dia dalam timezone yang sama.

            maaf jika komen ni panjang sangat. pening kepala aku lama dekat sini.
            jadi elok ku tulis untuk rujukan masa hadapan. dan diharapkan komen ini akan dibuang waktu vue build ke production.
            - Luqman
            */
        },
        registerBooking() {
            let { dateCheckIn, dateCheckOut, selectedRoom } = this.state;

            this.$store.dispatch("modalReservation/registerBooking", {
                roomId: selectedRoom.id,
                roomName: selectedRoom.label,
                start: dateCheckIn,
                end: dateCheckOut,
                timezone: this._timeZone
            });
        },
        async fetchProviders(staffId) {
            this.isLoadingProvider = true;

            let res = await Staff.getProviders();
            let providers = res.resource.data;
            let list = [];
            let user = null;

            for (let provider of providers) {
                list.push({
                    ...provider.actor,
                    is_default_provider: provider.is_default_provider || false
                });
            }

            let defaultProvider = list.find(
                provider => provider.is_default_provider === true
            );

            let currentUserProvider = list.find(p => p.id === staffId);

            if (defaultProvider && currentUserProvider) {
                user = defaultProvider;
            } else if (!defaultProvider && currentUserProvider) {
                user = currentUserProvider;
            } else if (defaultProvider && !currentUserProvider) {
                user = defaultProvider;
            } else {
                user = null;
            }

            this.state.providerList = list;
            this.state.selectedProvider = user;
            this.isLoadingProvider = false;
        }
    }
};
</script>
